import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Header from '../../components/Header';
import { api } from '../../api/client';
import {
  ArrowLeft,
  MapPin,
  CheckCircle,
  AlertTriangle,
  Save
} from 'lucide-react';
import PhotoUploader from '../../components/PhotoUploader';
import SignaturePad from '../../components/SignaturePad';
import { VerifyCheckOutButton } from '../../components/equipment/VerifyCheckOutButton';
import { useBack } from '../../hooks/useBack';
import { PreuseChecklist, type PreuseAnswer } from '../../components/equipment/PreuseChecklist';
import { describeDerivedResult, describeUnitConsequence, preuseRefusalSentence } from '../../lib/preuse';
import { unwrap } from '../../lib/api-envelope';
import type { PreuseAnswerWrite, PreuseCheckResult, PreuseForEquipment } from '../../types/preuse';

const CHECK_TYPES = [
  { value: 'pre_use', label: 'Pre-Use' },
  { value: 'post_use', label: 'Post-Use' },
];

export default function FieldEquipmentCheckForm() {
  const navigate = useNavigate();
  const goBack = useBack('/field/equipment');
  const location = useLocation();
  const queryClient = useQueryClient();
  const eq = (location.state as any)?.equipment;
  // A caller may say where to land after a save (the Pipeline & Facility daily
  // log sends its own path, so the hand comes back to link the check). Only an
  // in-app path is honoured; anything else keeps the old /field/equipment.
  const returnTo: unknown = (location.state as any)?.returnTo;
  const afterSave = typeof returnTo === 'string' && returnTo.startsWith('/') && !returnTo.startsWith('//') ? returnTo : '/field/equipment';

  const [form, setForm] = useState({
    equipmentId: eq?.id ?? '',
    checkType: 'pre_use',
    status: 'pass',
    notes: '',
    gpsLatitude: null as number | null,
    gpsLongitude: null as number | null,
    returnToYardId: '',
    photoUrls: [] as any[],
    signatureData: null as string | null,
  });
  const [gpsLoading, setGpsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // ── Pre-use template (api#1209) ─────────────────────────────────────────
  // The tenant's ACTIVE template for this unit's equipment type, or null. With
  // null (every casing unit today) the form below is exactly the old one: the
  // Status buttons, a `status` in the body, nothing else. With a template the
  // Status buttons are replaced by its items and NO status is sent — the
  // server derives it from the answers and returns it in `preuse`.
  const preuseQuery = useQuery({
    queryKey: ['preuse-for-equipment', form.equipmentId],
    queryFn: async () => unwrap<PreuseForEquipment>((await api.preuseTemplates.forEquipment(form.equipmentId)).data),
    enabled: !!form.equipmentId,
    retry: false,
  });
  const template = preuseQuery.data?.template ?? null;
  const [answers, setAnswers] = useState<Record<string, PreuseAnswer>>({});
  const [missing, setMissing] = useState<Set<string>>(new Set());
  const [preuseResult, setPreuseResult] = useState<PreuseCheckResult | null>(null);
  const [preuseSaved, setPreuseSaved] = useState(false);

  // Fetch yards for returnToYardId dropdown
  const { data: yardsData } = useQuery({
    queryKey: ['service-locations'],
    queryFn: () => api.serviceLocations.list().then(r => r.data.data ?? r.data ?? []),
  });
  const yards: any[] = yardsData ?? [];

  const mutation = useMutation({
    mutationFn: (data: any) => api.employee.createEquipmentCheck(data),
    onSuccess: (res: any, sent: any) => {
      queryClient.invalidateQueries({ queryKey: ['field-my-equipment'] });
      if (sent?.templateId) {
        // A templated check: show the server's verdict and let the hand leave
        // when they have read it, rather than navigating away under it.
        setPreuseResult((res?.data?.data?.preuse ?? null) as PreuseCheckResult | null);
        setPreuseSaved(true);
        setMessage(null);
        return;
      }
      setMessage({ type: 'success', text: 'Equipment check submitted' });
      setTimeout(() => navigate(afterSave, { replace: true }), 1500);
    },
    onError: (err: any, sent: any) => {
      setMessage({
        type: 'error',
        text: sent?.templateId
          ? preuseRefusalSentence(err, 'The check was not saved. Try again.')
          : err.response?.data?.error ?? 'Failed to submit check',
      });
    },
  });

  const getGPS = () => {
    if (!navigator.geolocation) return;
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm(f => ({ ...f, gpsLatitude: pos.coords.latitude, gpsLongitude: pos.coords.longitude }));
        setGpsLoading(false);
      },
      () => {
        setMessage({ type: 'error', text: 'Unable to get GPS location' });
        setGpsLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleSubmit = () => {
    if (!form.equipmentId) {
      setMessage({ type: 'error', text: 'No equipment selected' });
      return;
    }
    if (template) {
      const unanswered = template.items.filter((it) => it.required && !answers[it.itemKey]?.result).map((it) => it.itemKey);
      if (unanswered.length > 0) {
        setMissing(new Set(unanswered));
        setMessage({
          type: 'error',
          text: `Answer every required item before saving — ${unanswered.length} ${unanswered.length === 1 ? 'item is' : 'items are'} not answered.`,
        });
        return;
      }
      setMissing(new Set());
    }
    const payload: any = {
      equipmentId: form.equipmentId,
      checkType: form.checkType,
      notes: form.notes || null,
      gpsLatitude: form.gpsLatitude,
      gpsLongitude: form.gpsLongitude,
      photoUrls: form.photoUrls,
      signatureData: form.signatureData,
    };
    if (template) {
      // No `status`: the server works it out from the items, and refuses a
      // body status that disagrees with it.
      payload.templateId = template.id;
      payload.checklistItems = template.items
        .filter((it) => answers[it.itemKey]?.result)
        .map((it): PreuseAnswerWrite => {
          const a = answers[it.itemKey];
          const note = a.note.trim();
          return note ? { item_key: it.itemKey, result: a.result!, note } : { item_key: it.itemKey, result: a.result! };
        });
    } else {
      payload.status = form.status;
    }
    // Include returnToYardId for post-job checks
    if ((form.checkType === 'post_job' || form.checkType === 'post_use') && form.returnToYardId) {
      payload.returnToYardId = form.returnToYardId;
    }
    mutation.mutate(payload);
  };

  const showYardPicker = form.checkType === 'post_job' || form.checkType === 'post_use';

  return (
    <div className="animate-fade-in min-h-full bg-fieldiq-dark pb-[var(--bottom-chrome)]">
      <div className="max-w-[1920px] mx-auto">
        <Header />
        <div className="px-4 py-3 border-b border-fieldiq-border flex items-center gap-3">
          <button onClick={goBack} className="text-fieldiq-text">
            <ArrowLeft size={26} />
          </button>
          <div>
            <h1 className="text-xl font-bold text-white">Equipment Check</h1>
            {eq && <p className="text-xs text-fieldiq-muted">{eq.name}{eq.serialNumber ? ` - ${eq.serialNumber}` : eq.unit_number ? ` - ${eq.unit_number}` : ''}</p>}
          </div>
        </div>
      </div>

      <div className="max-w-[1920px] mx-auto px-4 py-4 space-y-4">
        {message && (
          <div className={`rounded-xl p-3 flex items-center gap-2 ${message.type === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
            {message.type === 'success' ? <CheckCircle size={18} /> : <AlertTriangle size={18} />}
            <span className="text-sm">{message.text}</span>
          </div>
        )}

        {preuseSaved ? (
          <PreuseSavedResult preuse={preuseResult} onDone={() => navigate(afterSave, { replace: true })} />
        ) : (
        <>
        <div className="bg-fieldiq-card rounded-xl p-4 space-y-4">
          {/* Check Type */}
          <div>
            <label className="text-xs text-fieldiq-muted mb-2 block">Check Type</label>
            <div className="grid grid-cols-2 gap-2">
              {CHECK_TYPES.map(ct => (
                <button
                  key={ct.value}
                  onClick={() => setForm(f => ({ ...f, checkType: ct.value }))}
                  className={`py-2 rounded-lg text-sm font-medium transition ${
                    form.checkType === ct.value
                      ? 'bg-fieldiq-accent text-fieldiq-dark'
                      : 'bg-fieldiq-dark text-fieldiq-text'
                  }`}
                >
                  {ct.label}
                </button>
              ))}
            </div>
          </div>

          {/* Status — or, when this unit's type has a live pre-use template, its items */}
          {preuseQuery.isLoading && form.equipmentId ? (
            <p className="text-sm text-fieldiq-muted" role="status">Loading this unit’s pre-use form…</p>
          ) : template ? (
            <div>
              <label className="text-xs text-fieldiq-muted mb-1 block">{template.name}</label>
              <p className="text-xs text-fieldiq-muted mb-3">
                Answer each item. A failed Critical item fails the whole check. The result is worked out when you save.
              </p>
              <PreuseChecklist
                items={template.items}
                answers={answers}
                missing={missing}
                onChange={(key, next) => {
                  setAnswers((all) => ({ ...all, [key]: next }));
                  if (next.result) setMissing((m) => { if (!m.has(key)) return m; const n = new Set(m); n.delete(key); return n; });
                }}
              />
            </div>
          ) : (
          <div>
            <label className="text-xs text-fieldiq-muted mb-2 block">Status</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 'pass', label: 'Pass', color: 'bg-green-500 text-white' },
                { value: 'needs_repair', label: 'Needs Repair', color: 'bg-amber-500 text-white' },
                { value: 'fail', label: 'Fail', color: 'bg-red-500 text-white' },
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setForm(f => ({ ...f, status: opt.value }))}
                  className={`py-2 rounded-lg text-sm font-medium transition ${
                    form.status === opt.value
                      ? opt.color
                      : 'bg-fieldiq-dark text-fieldiq-text'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {preuseQuery.isError && (
              <p className="text-xs text-fieldiq-muted mt-2" role="status">
                This unit’s pre-use form could not be loaded, so this is the plain check.
              </p>
            )}
          </div>
          )}

          {/* Return to Shop (post-job / post-use) */}
          {showYardPicker && (
            <div>
              <label className="text-xs text-fieldiq-muted mb-1 block">Return to Shop</label>
              <select
                value={form.returnToYardId}
                onChange={e => setForm(f => ({ ...f, returnToYardId: e.target.value }))}
                className="w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none"
              >
                <option value="">Select shop (optional)</option>
                {yards.map((y: any) => (
                  <option key={y.id} value={y.id}>{y.name} — {y.city}</option>
                ))}
              </select>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="text-xs text-fieldiq-muted mb-1 block">Notes</label>
            <textarea
              rows={4}
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Describe the condition, any issues found..."
              className="w-full bg-fieldiq-dark rounded-lg px-3 py-2 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none resize-none"
            />
          </div>

          {/* Verify & Check Out */}
          {form.equipmentId && (
            <div>
              <VerifyCheckOutButton
                equipmentId={form.equipmentId}
                hasReferencePhoto={!!(eq?.photoUrl ?? eq?.photo_url)}
                buttonClassName="w-full bg-amber-500 hover:bg-amber-400 text-white font-semibold py-3 px-4 rounded-lg min-h-[48px] text-base transition-colors"
                labels={{ matched: 'Check-out authorized', mismatch: 'Equipment mismatch' }}
              />
              <p className="text-xs text-fieldiq-muted mt-2">
                Optional — verifies this is the correct equipment
              </p>
            </div>
          )}

          {/* Photos */}
          <PhotoUploader
            entityType="equipment"
            entityId={form.equipmentId}
            value={form.photoUrls}
            onChange={(photos) => setForm(f => ({ ...f, photoUrls: photos }))}
            label="Equipment Photos"
          />

          {/* GPS */}
          <div>
            <label className="text-xs text-fieldiq-muted mb-2 block">GPS Location</label>
            {form.gpsLatitude ? (
              <div className="bg-fieldiq-dark rounded-lg px-3 py-2 text-sm flex items-center gap-2">
                <MapPin size={16} className="text-green-400" />
                <span>{form.gpsLatitude.toFixed(6)}, {form.gpsLongitude?.toFixed(6)}</span>
              </div>
            ) : (
              <button
                onClick={getGPS}
                disabled={gpsLoading}
                className="w-full bg-fieldiq-dark rounded-lg px-3 py-2 text-sm flex items-center justify-center gap-2 text-fieldiq-text"
              >
                <MapPin size={16} />
                {gpsLoading ? 'Getting location...' : 'Capture GPS Location'}
              </button>
            )}
          </div>
        </div>

        {/* Signature */}
        <SignaturePad
          label="Inspector Signature"
          value={form.signatureData ?? undefined}
          onSave={(dataUrl) => setForm(f => ({ ...f, signatureData: dataUrl }))}
          onClear={() => setForm(f => ({ ...f, signatureData: null }))}
        />

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={mutation.isPending}
          className="w-full bg-fieldiq-accent text-fieldiq-dark font-semibold py-3 rounded-xl flex items-center justify-center gap-2"
        >
          <Save size={18} /> {mutation.isPending ? 'Submitting...' : 'Submit Check'}
        </button>
        </>
        )}
      </div>
    </div>
  );
}

/**
 * After a templated check saves: the server's verdict, from `derived_status`
 * and its counts. It says nothing about what happened to the unit — that is
 * the API's to report (a follow-up may add a field for it), not this screen's
 * to guess.
 */
function PreuseSavedResult({ preuse, onDone }: { preuse: PreuseCheckResult | null; onDone: () => void }) {
  const copy = describeDerivedResult(preuse);
  const unit = describeUnitConsequence(preuse);
  const tone =
    copy.tone === 'pass' ? 'bg-green-500/20 border-green-500' :
    copy.tone === 'fail' ? 'bg-red-500/20 border-red-500' :
    copy.tone === 'needs_repair' ? 'bg-amber-500/20 border-amber-500' :
    'bg-fieldiq-card border-fieldiq-border';
  return (
    <div className="space-y-4" data-testid="preuse-result">
      <div className={`rounded-xl border p-4 ${tone}`} role="status">
        <p className="text-xs text-fieldiq-muted">Equipment check saved</p>
        <p className="mt-1 text-lg font-bold text-fieldiq-text">{copy.title}</p>
        <p className="mt-1 text-sm text-fieldiq-text">{copy.detail}</p>
        {unit.map((line) => (
          <p key={line} className="mt-1 text-sm font-semibold text-fieldiq-text" data-testid="preuse-unit-consequence">{line}</p>
        ))}
      </div>
      <button
        type="button"
        onClick={onDone}
        className="w-full bg-fieldiq-accent text-fieldiq-dark font-semibold py-3 rounded-xl"
      >
        Done
      </button>
    </div>
  );
}
